<template>
  <component :is="tag" :id="uid" ref="canvasRef" :class="canvasClass" />
</template>

<script lang="ts">
export default {
  name: "MDBChart",
};
</script>

<script setup lang="ts">
import generateCharts from "./generateCharts";
import { getUID } from "../../../../components/utils/getUID";
import {
  onBeforeMount,
  onMounted,
  onUnmounted,
  ref,
  watch,
  PropType,
  useAttrs,
  computed,
} from "vue";
import {
  ChartTypeRegistry,
  ChartData,
  ChartConfiguration,
} from "chart.js/auto";
import defaultDarkOptions from "./chartsDefaultDarkmodeOptions";

const props = defineProps({
  tag: {
    type: String,
    default: "canvas",
  },
  type: String,
  data: Object as PropType<ChartData>,
  options: Object as PropType<ChartConfiguration["options"]>,
  datalabels: {
    type: Boolean,
    default: false,
  },
  id: String,
  canvasClass: String,
  disableDarkMode: Boolean,
  darkTicksColor: {
    type: String,
    default: defaultDarkOptions.darkTicksColor,
  },
  darkLabelColor: {
    type: String,
    default: defaultDarkOptions.darkLabelColor,
  },
  darkGridLinesColor: {
    type: String,
    default: defaultDarkOptions.darkGridLinesColor,
  },
  darkBgColor: {
    type: String,
    default: defaultDarkOptions.darkBgColor,
  },
  darkBgColorLight: {
    type: String,
    default: defaultDarkOptions.darkBgColorLight,
  },
  darkModeOptions: Object as PropType<ChartConfiguration["options"]>,
});

const attrs = useAttrs();

const canvasRef = ref<HTMLElement | HTMLCanvasElement | null>(null);
const uid = props.id || getUID("MDBChart-");

// References to chart functions

const setupChartsFunc = ref<(() => void) | null>(null);
const renderChartFunc = ref<(() => void) | null>(null);
const addDataLabelsFunc = ref<(() => void) | null>(null);
const updateChartFunc = ref<((...args: any[]) => void) | null>(null);
const changeModeFunc = ref<((mode: "light" | "dark") => void) | null>(null);
const updateDarkmodeOptionsFunc = ref<
  ((newDarkOptions: object) => void) | null
>(null);

interface ChartsFunctions {
  setupCharts: () => void;
  renderChart: () => void;
  addDataLabels: () => void;
  updateChart: (...args: any[]) => void;
  changeMode: (mode: "light" | "dark") => void;
  updateDarkmodeOptions: (newDarkOptions: object) => void;
}

const getChartsFunctions = ({
  setupCharts,
  renderChart,
  addDataLabels,
  updateChart,
  changeMode,
  updateDarkmodeOptions,
}: ChartsFunctions) => {
  setupChartsFunc.value = setupCharts;
  renderChartFunc.value = renderChart;
  addDataLabelsFunc.value = addDataLabels;
  updateChartFunc.value = updateChart;
  changeModeFunc.value = changeMode;
  updateDarkmodeOptionsFunc.value = updateDarkmodeOptions;
};

watch(
  () => props.data,
  (newData: object) => {
    updateChartFunc.value?.(newData, props.options || {});
  }
);

watch(
  () => props.options,
  (newOptions: object) => {
    const data = props.data || {};
    updateChartFunc.value?.(data, newOptions);
  }
);

// Dark mode handling
watch(
  () => props.darkModeOptions,
  (newDarkOptions: object) => {
    if (props.disableDarkMode) {
      return;
    }

    updateDarkmodeOptionsFunc.value?.(newDarkOptions);
  }
);

const observer = ref<MutationObserver | null>(null);

const observerCallback = (mutationList: MutationRecord[]) => {
  mutationList.forEach((mutation) => {
    if (
      mutation.type === "attributes" &&
      mutation.attributeName === "data-mdb-theme"
    ) {
      const colorMode =
        (mutation.target as HTMLElement).getAttribute("data-mdb-theme") ===
        "dark"
          ? "dark"
          : "light";

      changeModeFunc.value?.(colorMode);
    }
  });
};

const getSystemColorMode = () => {
  if (props.disableDarkMode) {
    return "light";
  }

  return attrs["data-mdb-theme"] || localStorage.theme || "light";
};

onBeforeMount(() => {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    props.disableDarkMode
  ) {
    return;
  }

  observer.value = new MutationObserver(observerCallback);
  const htmlElement = document.querySelector("html");

  if (htmlElement) {
    observer.value.observe(htmlElement, {
      attributes: true,
      attributeFilter: ["data-mdb-theme"],
    });
  }
});

const darkPropsComputed = computed(() => ({
  darkTicksColor: props.darkTicksColor,
  darkLabelColor: props.darkLabelColor,
  darkGridLinesColor: props.darkGridLinesColor,
  darkBgColor: props.darkBgColor,
  darkBgColorLight: props.darkBgColorLight,
}));

onMounted(() => {
  // chart functions must be initialized on mount to properly pass canvasRef value to Charts.js
  if (canvasRef.value) {
    const chartsFunctions = generateCharts(
      canvasRef.value,
      props.type as keyof ChartTypeRegistry,
      props.data,
      props.options,
      props.darkModeOptions,
      darkPropsComputed.value,
      getSystemColorMode()
    );

    getChartsFunctions(chartsFunctions);

    if (props.datalabels) {
      addDataLabelsFunc.value?.();
    }

    renderChartFunc.value?.();
  }
});

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
    observer.value = null;
  }
});
</script>
